import { Badge, Box, Button, Card, Flex, Skeleton } from '@radix-ui/themes';
import { PitchPerformanceHelper } from 'classes/helpers/pitch-performance.helper';
import { CommonSwitchButton } from 'components/common/buttons/switch-button';
import { ErrorBoundary } from 'components/common/error-boundary';
import {
  AXIS_EXTENT_LABELS,
  AxisExtentLabel,
} from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/break-stdev/axis-label';
import { BreakCross } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/break-stdev/cross';
import { BreakDot } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/break-stdev/dot';
import { BreakReferenceArea } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/break-stdev/reference-area';
import { BreakTarget } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/break-stdev/target';
import { BreakTooltip } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/break-stdev/tooltip';
import { PitchPerfPlotHwdLegend } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/hwd-legend';
import { PlotHeader } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plot-header';
import { usePlotHover } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots/plot-hover';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import {
  AXIS_COLOR_ON_DARK,
  AXIS_COLOR_ON_LIGHT,
  GRID_COLOR_ON_DARK,
  GRID_COLOR_ON_LIGHT,
} from 'enums/canvas';
import { BREAK_PLOT_MAX_IN } from 'enums/pitch-performance.enums';
import { t } from 'i18next';
import {
  IBreakPoint,
  IPitchPerfPlot,
  IPlotCoords,
  IPlotCoordsLine,
  IPlotThresholdCircle,
  IScatterShapeBreakPoint,
  PREVIOUS_MS_FILL,
  PREVIOUS_MS_STROKE,
} from 'interfaces/i-pitch-performance';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BallType } from 'lib_ts/enums/machine.enums';
import {
  MAX_BREAKS_DELTA_IN,
  MAX_BREAKS_DELTA_IN_TRAJEKT_36,
} from 'lib_ts/enums/pitch-performance';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ITrajektRefBreak } from 'lib_ts/interfaces/pitches';
import { compact, isNil, range } from 'lodash';
import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useMiscStore from 'stores/use-misc-store';

const ENABLE_STATCAST_STD_BREAK_CIRCLES = true;

const DRAW_CONNECTORS = false;

// how many standard deviations to use for determining % of shots within in-game variance
const STATCAST_STD_FACTOR = 2;

const PLOT_TICK_INTERVAL_IN = 10;

const PLOT_DOMAIN: [number, number] = [-BREAK_PLOT_MAX_IN, BREAK_PLOT_MAX_IN];

const PLOT_TICKS = range(
  -BREAK_PLOT_MAX_IN,
  BREAK_PLOT_MAX_IN + PLOT_TICK_INTERVAL_IN,
  PLOT_TICK_INTERVAL_IN
);

/**
 * The plot area has to stay square so that break error circles are drawn as
 * circles instead of ellipses. With aspect={1} on the container that only holds
 * while the margin allowance is identical horizontally and vertically, so both
 * axes are hidden and the extents are labelled against the x=0 / y=0 lines.
 */
const CHART_MARGIN = { top: 0, right: 0, bottom: 0, left: 0 };

const REF_GROUP_OPACITY = 0.75;

const toPlotCoords = (value: ITrajektRefBreak): IPlotCoords => ({
  // horizontal break is mirrored so the plot reads from the hitter's point of view
  x: -value.xInches,
  y: value.zInches,
});

const isWithinPlot = (value: IPlotCoords) =>
  Math.abs(value.x) <= BREAK_PLOT_MAX_IN &&
  Math.abs(value.y) <= BREAK_PLOT_MAX_IN;

export const PitchPerfPlotBreaks = (props: IPitchPerfPlot) => {
  const loading = usePitchPerformanceStore((s) => s.loading);
  const selectedShotIds = usePitchPerformanceStore((s) => s.selectedShotIds);
  const filters = usePitchPerformanceStore((s) => s.filters);
  const showThresholds = usePitchPerformanceStore((s) => s.showThresholds);
  const statsPitchGroups = usePitchPerformanceStore((s) => s.statsPitchGroups);
  const statsPitcherPk = usePitchPerformanceStore((s) => s.statsPitcherPk);

  const [isAveraged, setIsAveraged] = useState(true);
  const { pointOpacity, hoverHandlers } = usePlotHover();

  // todo: do we need to add back a way to toggle them off or do we always show targets?
  const [showTargets] = useState(true);

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  const axisColor = isDarkMode ? AXIS_COLOR_ON_DARK : AXIS_COLOR_ON_LIGHT;
  const gridColor = isDarkMode ? GRID_COLOR_ON_DARK : GRID_COLOR_ON_LIGHT;

  const MAX_BREAKS_DELTA_INCHES =
    filters.ball_type === BallType.Trajekt_36
      ? MAX_BREAKS_DELTA_IN_TRAJEKT_36
      : MAX_BREAKS_DELTA_IN;

  const visibleStatsGroups = useMemo(
    () =>
      statsPitchGroups
        .filter((g) => !g.invisible)
        .filter((g) => props.groups.some((fg) => fg.pitch.type === g.type)),
    [statsPitchGroups, props.groups]
  );

  const statcastPoints = useMemo(
    () =>
      compact(
        visibleStatsGroups.flatMap((statsGroup) =>
          statsGroup.breaks.map((b) => {
            const target = toPlotCoords(b);

            if (!isWithinPlot(target)) {
              return undefined;
            }

            const point: IBreakPoint = {
              fillRgb: statsGroup.rgb,
              strokeRgb: statsGroup.rgb,
              itemKey: PitchPerformanceHelper.typeHoverKey(statsGroup.type),
              highlightKeys: [
                PitchPerformanceHelper.typeHoverKey(statsGroup.type),
                ...props.groups
                  .filter((group) => group.pitch.type === statsGroup.type)
                  .map((group) =>
                    PitchPerformanceHelper.groupHoverKey(group._id)
                  ),
              ],
              pitchName: `${t(statsGroup.typeName)} (In-Game)`,
              msHash: undefined,
              shotId: undefined,
              isTarget: false,
              x: target.x,
              y: target.y,
            };

            return point;
          })
        )
      ),
    [props.groups, visibleStatsGroups]
  );

  const targetPoints = useMemo(() => {
    if (!showTargets) {
      return [];
    }

    return compact(
      props.groups.map((group) => {
        const target = toPlotCoords(group.target.break);

        if (!isWithinPlot(target)) {
          return undefined;
        }

        const point: IBreakPoint = {
          ...PitchPerformanceHelper.hoverKeys(group),
          fillRgb: group.rgb,
          strokeRgb: group.rgb,
          pitchName: `${group.pitch.name} (Target)`,
          msHash: group.target.ms.matching_hash,
          shotId: undefined,
          isTarget: true,
          x: target.x,
          y: target.y,
        };

        return point;
      })
    );
  }, [props.groups, showTargets]);

  const actualPoints = useMemo(() => {
    const output: IBreakPoint[] = [];

    for (const group of props.groups) {
      const target = toPlotCoords(group.target.break);

      if (isAveraged) {
        const average = toPlotCoords(group.average.break);

        if (!isWithinPlot(average)) {
          continue;
        }

        output.push({
          ...PitchPerformanceHelper.hoverKeys(group),
          fillRgb:
            !props.highlightCurrent || group.isCurrentMs
              ? group.rgb
              : PREVIOUS_MS_FILL,
          strokeRgb:
            !props.highlightCurrent || group.isCurrentMs
              ? group.rgb
              : PREVIOUS_MS_STROKE,

          pitchName: `${group.pitch.name} (Average)`,
          msHash: group.target.ms.matching_hash,
          shotId: undefined,
          isTarget: false,

          x: average.x,
          y: average.y,

          target: showTargets ? target : undefined,
          delta: showTargets
            ? {
                x: average.x - target.x,
                y: average.y - target.y,
              }
            : undefined,
        });

        // move onto the next pitch w/o interacting w/ individual shots while averaging
        continue;
      }

      // if the user has selected specific shots, only plot those
      const shots = group.rotatedShots.filter(
        (s) => selectedShotIds.length === 0 || selectedShotIds.includes(s._id)
      );

      for (const shot of shots) {
        if (!shot.traj) {
          continue;
        }

        const actual = toPlotCoords(TrajHelper.getBreaksFromShot(shot));

        if (!isWithinPlot(actual)) {
          continue;
        }

        output.push({
          ...PitchPerformanceHelper.hoverKeys(group),
          fillRgb:
            !props.highlightCurrent || group.isCurrentMs
              ? group.rgb
              : PREVIOUS_MS_FILL,
          strokeRgb:
            !props.highlightCurrent || group.isCurrentMs
              ? group.rgb
              : PREVIOUS_MS_STROKE,

          pitchName: group.pitch.name,
          msHash: group.target.ms.matching_hash,
          shotId: shot._id,
          isTarget: false,

          x: actual.x,
          y: actual.y,

          target: showTargets ? target : undefined,
          delta: showTargets
            ? {
                x: actual.x - target.x,
                y: actual.y - target.y,
              }
            : undefined,
        });
      }
    }

    return output;
  }, [
    props.groups,
    props.highlightCurrent,
    isAveraged,
    showTargets,
    selectedShotIds,
  ]);

  const refPoints = useMemo(() => {
    if (!props.refGroups) {
      return [];
    }

    const output: IBreakPoint[] = [];

    for (const refGroup of props.refGroups) {
      if (isAveraged) {
        const average = toPlotCoords(refGroup.average.break);

        if (!isWithinPlot(average)) {
          continue;
        }

        output.push({
          ...PitchPerformanceHelper.refHoverKeys(refGroup),
          fillRgb: refGroup.rgb,
          strokeRgb: refGroup.rgb,
          pitchName: `${refGroup.pitch.name} (Reference Average)`,
          msHash: refGroup.target.ms.matching_hash,
          shotId: undefined,
          isTarget: false,
          x: average.x,
          y: average.y,
        });
        continue;
      }

      // if the user has selected specific shots, only plot those
      const shots = refGroup.rotatedShots.filter(
        ({ _id }) =>
          selectedShotIds.length === 0 || selectedShotIds.includes(_id)
      );

      for (const shot of shots) {
        if (!shot.traj) {
          continue;
        }

        const actual = toPlotCoords(TrajHelper.getBreaksFromShot(shot));

        if (!isWithinPlot(actual)) {
          continue;
        }

        output.push({
          ...PitchPerformanceHelper.refHoverKeys(refGroup),
          fillRgb: refGroup.rgb,
          strokeRgb: refGroup.rgb,
          pitchName: `${refGroup.pitch.name} (Reference)`,
          msHash: refGroup.target.ms.matching_hash,
          shotId: shot._id,
          isTarget: false,
          x: actual.x,
          y: actual.y,
        });
      }
    }

    return output;
  }, [props.refGroups, isAveraged, selectedShotIds]);

  // acceptable bubbles around each target
  const thresholdCircles = useMemo(() => {
    if (!showThresholds) {
      return [];
    }

    return props.groups.map((group) => {
      const target = toPlotCoords(group.target.break);

      const circle: IPlotThresholdCircle = {
        ...PitchPerformanceHelper.hoverKeys(group),
        key: group._id,
        radius: MAX_BREAKS_DELTA_INCHES,
        rgb: group.rgb,
        x: target.x,
        y: target.y,
      };

      return circle;
    });
  }, [props.groups, showThresholds, MAX_BREAKS_DELTA_INCHES]);

  // in-game variance around each target
  const stdCircles = useMemo(() => {
    if (!ENABLE_STATCAST_STD_BREAK_CIRCLES) {
      return [];
    }

    const output: IPlotThresholdCircle[] = [];

    for (const group of props.groups) {
      const statsGroup = visibleStatsGroups.find(
        (m) => m.type === group.pitch.type
      );

      if (!statsGroup || isNil(statsGroup.stdBreakErrorIn)) {
        continue;
      }

      const target = toPlotCoords(group.target.break);

      output.push({
        ...PitchPerformanceHelper.hoverKeys(group),
        key: group._id,
        radius: statsGroup.stdBreakErrorIn * STATCAST_STD_FACTOR,
        rgb: statsGroup.rgb,
        x: target.x,
        y: target.y,
      });
    }

    return output;
  }, [props.groups, visibleStatsGroups]);

  // lines from each average to its target
  const connectors = useMemo(() => {
    if (!isAveraged || !showTargets) {
      return [];
    }

    return props.groups.map((group) => {
      const connector: IPlotCoordsLine = {
        ...PitchPerformanceHelper.hoverKeys(group),
        key: group._id,
        from: toPlotCoords(group.average.break),
        to: toPlotCoords(group.target.break),
        rgb: group.rgb,
      };

      return connector;
    });
  }, [props.groups, isAveraged, showTargets]);

  const shotsWithinStatcast = useMemo(() => {
    const output = {
      inside: 0,
      total: 0,
    };

    for (const group of props.groups) {
      const statsGroup = statsPitchGroups.find(
        (m) => m.type === group.pitch.type
      );

      if (statsGroup?.stdBreakErrorIn === undefined) {
        continue;
      }

      const shots = group.rotatedShots.filter(
        (s) => selectedShotIds.length === 0 || selectedShotIds.includes(s._id)
      );

      for (const shot of shots) {
        const actual = TrajHelper.getBreaksFromShot(shot);

        const errorX = actual.xInches - group.target.break.xInches;
        const errorZ = actual.zInches - group.target.break.zInches;

        const errorIn = Math.sqrt(Math.pow(errorX, 2) + Math.pow(errorZ, 2));

        if (errorIn <= statsGroup.stdBreakErrorIn * STATCAST_STD_FACTOR) {
          output.inside++;
        }

        output.total++;
      }
    }

    return output;
  }, [props.groups, statsPitchGroups, selectedShotIds]);

  return (
    <ErrorBoundary identifier="PitchPerfPlotBreaks">
      <Skeleton loading={loading}>
        <Flex direction="column" gap="3" align="stretch">
          <PlotHeader
            title="Break"
            subtitle="Scatter plot of breaks (in) with Hz and Vt components"
          >
            <Flex gap="2" align="center">
              {shotsWithinStatcast.total > 0 && (
                <Badge
                  className="cursor-help"
                  color={RADIX.COLOR.SUCCESS}
                  title={`${shotsWithinStatcast.inside} / ${shotsWithinStatcast.total} shots within`}
                >
                  {(
                    100 *
                    (shotsWithinStatcast.inside / shotsWithinStatcast.total)
                  ).toFixed(0)}
                  % Within In-Game Variance
                </Badge>
              )}

              <CommonSwitchButton
                id="toggle-averages"
                switchPosition="right"
                variant="soft"
                size="1"
                displayTrue="Averages"
                displayFalse="Averages"
                value={isAveraged}
                onChange={(v) => setIsAveraged(v)}
              />

              {!props.refGroups && (
                <Button
                  size="1"
                  variant="soft"
                  color={
                    statsPitcherPk ? RADIX.COLOR.ACCENT : RADIX.COLOR.NEUTRAL
                  }
                  onClick={() =>
                    usePitchPerformanceStore
                      .getState()
                      .setStatsDialog(Date.now())
                  }
                >
                  <Flex gap="1" align="center">
                    <img width="16" src="/img/statcast-logo.svg" />
                    <Box>Statcast</Box>
                  </Flex>
                </Button>
              )}
            </Flex>
          </PlotHeader>

          <Card className="no-padding">
            <ResponsiveContainer width="100%" aspect={1}>
              <ScatterChart margin={CHART_MARGIN}>
                <CartesianGrid stroke={gridColor} />

                <XAxis
                  type="number"
                  dataKey="x"
                  name="Hz Break"
                  domain={PLOT_DOMAIN}
                  ticks={PLOT_TICKS}
                  hide // ? hide default labels b/c they can only be drawn along the axes, outside the plot
                />

                <YAxis
                  type="number"
                  dataKey="y"
                  name="Vt Break"
                  domain={PLOT_DOMAIN}
                  ticks={PLOT_TICKS}
                  hide // ? hide default labels b/c they can only be drawn along the axes, outside the plot
                />

                <ReferenceLine x={0} stroke={axisColor} />
                <ReferenceLine y={0} stroke={axisColor} />

                {AXIS_EXTENT_LABELS.map((label) => (
                  <ReferenceDot
                    key={`axis-label-${label.key}`}
                    x={label.x}
                    y={label.y}
                    r={0}
                    fill={axisColor}
                    stroke="none"
                    ifOverflow="visible"
                    shape={<AxisExtentLabel {...label} />}
                  />
                ))}

                {thresholdCircles.map((circle) => (
                  <ReferenceArea
                    key={`threshold-${circle.key}`}
                    x1={circle.x - circle.radius}
                    x2={circle.x + circle.radius}
                    y1={circle.y - circle.radius}
                    y2={circle.y + circle.radius}
                    ifOverflow="hidden"
                    fill={`rgb(${circle.rgb})`}
                    fillOpacity={pointOpacity(circle, 0.25)}
                    stroke={`rgb(${circle.rgb})`}
                    strokeOpacity={pointOpacity(circle, 0.75)}
                    strokeWidth={1}
                    shape={<BreakReferenceArea />}
                  />
                ))}

                {stdCircles.map((circle) => (
                  <ReferenceArea
                    key={`std-${circle.key}`}
                    x1={circle.x - circle.radius}
                    x2={circle.x + circle.radius}
                    y1={circle.y - circle.radius}
                    y2={circle.y + circle.radius}
                    ifOverflow="hidden"
                    fill="none"
                    stroke={`rgb(${circle.rgb})`}
                    strokeOpacity={pointOpacity(circle, 0.5)}
                    strokeWidth={1}
                    strokeDasharray="8 8"
                    shape={<BreakReferenceArea />}
                  />
                ))}

                {DRAW_CONNECTORS &&
                  connectors.map((connector) => (
                    <ReferenceLine
                      key={`connector-${connector.key}`}
                      segment={[
                        {
                          x: connector.from.x,
                          y: connector.from.y,
                        },
                        {
                          x: connector.to.x,
                          y: connector.to.y,
                        },
                      ]}
                      ifOverflow="hidden"
                      stroke={`rgb(${connector.rgb})`}
                      strokeOpacity={pointOpacity(connector)}
                      strokeDasharray="8 4"
                    />
                  ))}

                <Scatter
                  name="In-Game"
                  data={statcastPoints}
                  shape={(point: IScatterShapeBreakPoint) => (
                    <BreakCross
                      cx={point.cx}
                      cy={point.cy}
                      payload={point.payload}
                      opacity={pointOpacity(point.payload)}
                      {...hoverHandlers(point.payload?.itemKey)}
                    />
                  )}
                  isAnimationActive={false}
                />

                <Scatter
                  name="Reference"
                  data={refPoints}
                  shape={(point: IScatterShapeBreakPoint) => (
                    <BreakCross
                      cx={point.cx}
                      cy={point.cy}
                      payload={point.payload}
                      opacity={pointOpacity(point.payload, REF_GROUP_OPACITY)}
                      {...hoverHandlers(point.payload?.itemKey)}
                    />
                  )}
                  isAnimationActive={false}
                />

                <Scatter
                  name="Delivered"
                  data={actualPoints}
                  shape={(point: IScatterShapeBreakPoint) => (
                    <BreakDot
                      cx={point.cx}
                      cy={point.cy}
                      payload={point.payload}
                      opacity={pointOpacity(point.payload)}
                      {...hoverHandlers(point.payload?.itemKey)}
                    />
                  )}
                  isAnimationActive={false}
                />

                <Scatter
                  name="Target"
                  data={targetPoints}
                  shape={(point: IScatterShapeBreakPoint) => (
                    <BreakTarget
                      cx={point.cx}
                      cy={point.cy}
                      payload={point.payload}
                      opacity={pointOpacity(point.payload)}
                      {...hoverHandlers(point.payload?.itemKey)}
                    />
                  )}
                  isAnimationActive={false}
                />

                <Tooltip
                  content={<BreakTooltip />}
                  animationDuration={0}
                  cursor={false}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </Card>

          {props.isHwDiagnostic && <PitchPerfPlotHwdLegend />}
        </Flex>
      </Skeleton>
    </ErrorBoundary>
  );
};
